import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, X, Link2, MessageCircle, Facebook, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { ease } from "./Primitives";
import { useT } from "@/lib/i18n";

export const ShareButton = () => {
  const t = useT().share;
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const url = window.location.origin;
  const enc = encodeURIComponent;
  const links = [
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: `https://wa.me/?text=${enc(`${t.text} ${url}`)}` },
    { id: "facebook", label: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "linkedin", label: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(t.copied); } catch { toast.error(url); }
    setOpen(false);
  };

  const main = async () => {
    if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
      try { await navigator.share({ title: "Lene Mauro", text: t.text, url }); } catch { /* cancelled */ }
      return;
    }
    setOpen(!open);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.5, ease }}
          className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
          <AnimatePresence>
            {open && (
              <motion.div data-testid="share-menu" initial={{ opacity: 0, scale: 0.9, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 10 }}
                transition={{ duration: 0.3, ease }}
                className="rounded-2xl border border-[var(--line)] bg-[#0c0a09]/90 backdrop-blur-xl p-2 w-52 origin-bottom-right">
                {links.map(({ id, label, Icon, href }) => (
                  <a key={id} href={href} target="_blank" rel="noopener noreferrer" data-testid={`share-${id}-link`} onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-200 hover:bg-white/5 hover:text-[var(--gold)] transition-colors">
                    <Icon size={16} /> {label}
                  </a>
                ))}
                <button onClick={copy} data-testid="share-copy-btn"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-200 hover:bg-white/5 hover:text-[var(--gold)] transition-colors">
                  <Link2 size={16} /> {t.copy}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <button onClick={main} data-testid="share-btn" aria-label={t.label}
            className="btn-gold !px-5 !py-3 text-sm shadow-[0_14px_40px_-12px_rgba(230,197,148,0.55)]">
            {open ? <X size={18} /> : <Share2 size={18} />} {t.label}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
